import { Injectable } from '@angular/core';
import { switchMap, map } from 'rxjs/operators';
import { Observable, BehaviorSubject } from 'rxjs';
import { SERVER_URL } from 'src/environments/environment';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Platform } from '@ionic/angular';
import { Router } from '@angular/router';
 
export interface notifications {
  userId: number,
  type: number,
  postId: number | null,
  clientId: number | null,
  profile: string,
  content: string,
  createdAt: Date,
}

// Notification types
// export const NOTIFICATION_TYPES = {
//   GENERAL: 0,
//   KUDOS: 1,
//   FEEDBACK: 2,
//   CHAT: 3,
//   ACTION: 4,
// };
 
export interface Notification {
  id?: number;
  user_id: number;
  type: number;
  post_id: number | null;
  client_id: number | null;
  profile: string;
  content: string;
  title?: string;
  created_at: Date;
  is_read?: boolean;
  from_user_id?: number;
  from_user_name?: string;
}

// Notification types - ✅ ADD more types
export const NOTIFICATION_TYPES = {
  GENERAL: 0,
  KUDOS: 1,
  FEEDBACK: 2,
  CHAT: 3,
  ACTION: 4,
  REVIEW: 5,      // ✅ NEW
  TRAINING: 6,    // ✅ NEW
  MEETING: 7,     // ✅ NEW
  AWARD: 8,
  IDEA: 9,
  ANNOUNCEMENT: 10,
};

// ✅ ADD: Notification colors
export const NOTIFICATION_COLORS: { [key: number]: string } = {
  [NOTIFICATION_TYPES.GENERAL]: '#6C757D',
  [NOTIFICATION_TYPES.KUDOS]: '#FFC107',
  [NOTIFICATION_TYPES.FEEDBACK]: '#007BFF',
  [NOTIFICATION_TYPES.CHAT]: '#17A2B8',
  [NOTIFICATION_TYPES.ACTION]: '#FF4444',
  [NOTIFICATION_TYPES.REVIEW]: '#28A745',
  [NOTIFICATION_TYPES.TRAINING]: '#6F42C1',
  [NOTIFICATION_TYPES.MEETING]: '#6C757D',
  [NOTIFICATION_TYPES.AWARD]: '#FF8E00',
  [NOTIFICATION_TYPES.IDEA]: '#3873AF',
  [NOTIFICATION_TYPES.ANNOUNCEMENT]: '#3873AF',
};

// ✅ ADD: Notification icons
export const NOTIFICATION_ICONS: { [key: number]: string } = {
  [NOTIFICATION_TYPES.GENERAL]: 'notifications-outline',
  [NOTIFICATION_TYPES.KUDOS]: 'thumbs-up-outline',
  [NOTIFICATION_TYPES.FEEDBACK]: 'chatbubbles-outline',
  [NOTIFICATION_TYPES.CHAT]: 'chatbubble-outline',
  [NOTIFICATION_TYPES.ACTION]: 'clipboard-outline',
  [NOTIFICATION_TYPES.REVIEW]: 'star-outline',
  [NOTIFICATION_TYPES.TRAINING]: 'school-outline',
  [NOTIFICATION_TYPES.MEETING]: 'people-outline',
  [NOTIFICATION_TYPES.AWARD]: 'ribbon-outline',
  [NOTIFICATION_TYPES.IDEA]: 'bulb-outline',
  [NOTIFICATION_TYPES.ANNOUNCEMENT]: 'megaphone-outline',
};

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  notifications: any;
  // Preserve the active host port (for example localhost:5001) so the
  // notification API is requested from the same server that served the app.
  private apiUrl = window.location.origin + '/api';

  // ✅ Track unread chat messages
  public unreadChatCount$ = new BehaviorSubject<number>(0);
  
  // ✅ Track new incoming notifications for in-app alerts
  public newNotification$ = new BehaviorSubject<any>(null);
  public unreadCount$ = new BehaviorSubject<number>(0);

  constructor(
    private http: HttpClient,
    private platform: Platform,
    private router: Router
  ) {}
 
  // constructor(private http: HttpClient,) {}
  // notifications: any;
  // private apiUrl = window.location.protocol + '//' + window.location.hostname  + '/api';


  addnotifications(content: string, id: number, type: number, postId: number | null, profile: string, clientId?: number | null) {
  const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  const body = { 
    content, 
    user_id: id,        
    type, 
    post_id: postId,    
    profile,
    client_id: clientId 
  };
  return this.http.post(`${this.apiUrl}/notifications`, body, { headers });
  }

  getnotifications(userid) {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.get<notifications[]>(`${this.apiUrl}/notifications/${userid}`, { headers });
  }

    sendChatNotification(
    toUserId: number,
    fromUserId: number,
    fromName: string,
    message: string,
    clientId: number
  ) {
    const content = `${fromName}: ${message.length > 50 ? message.substring(0, 50) + '...' : message}`;
    
    // Add notification to database
    return this.addnotifications(
      content,
      toUserId,
      NOTIFICATION_TYPES.CHAT,
      fromUserId, // Store sender's ID in postId field for navigation
      '', // profile image URL if available
      clientId
    );
  }

  // ✅ NEW: Get unread chat notifications count
  getUnreadChatCount(userId: number): Observable<number> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams()
      .set('user_id', userId.toString())
      .set('type', NOTIFICATION_TYPES.CHAT.toString())
      .set('unread', 'true');
    
    return this.http.get<number>(`${this.apiUrl}/notifications/unread-count`, { headers, params });
  }

  // ✅ NEW: Mark chat notifications as read
  markChatAsRead(userId: number, fromUserId: number) {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const body = {
      user_id: userId,
      from_user_id: fromUserId,
      type: NOTIFICATION_TYPES.CHAT
    };
    return this.http.post(`${this.apiUrl}/notifications/mark-read`, body, { headers });
  }

  // ✅ NEW: Update unread count
  updateUnreadCount(count: number) {
    this.unreadChatCount$.next(count);
  }

  // ✅ NEW: Increment unread count
  incrementUnreadCount() {
    this.unreadChatCount$.next(this.unreadChatCount$.value + 1);
  }

  // ✅ NEW: Reset unread count
  resetUnreadCount() {
    this.unreadChatCount$.next(0);
  }

  // ✅ NEW: Show in-app notification
  showInAppNotification(notification: any) {
    this.newNotification$.next(notification);
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
      if (this.newNotification$.value === notification) {
        this.newNotification$.next(null);
      }
    }, 5000);
  }

  // ═══════════════════════════════════════════════════════════════
  // ✅ ADD ALL METHODS BELOW TO YOUR EXISTING SERVICE
  // ═══════════════════════════════════════════════════════════════

  // ✅ ADD: Send kudos notification
  sendKudosNotification(
    toUserId: number,
    fromUserId: number,
    fromUserName: string,
    clientId: number,
    kudosType: string,
    profile: string = ''
  ) {
    const content = `${fromUserName} gave you kudos for ${kudosType}`;
    return this.addnotifications(
      content,
      toUserId,
      NOTIFICATION_TYPES.KUDOS,
      null,
      profile,
      clientId
    );
  }

  // ✅ ADD: Send feedback notification
  sendFeedbackNotification(
    toUserId: number,
    fromUserId: number,
    fromUserName: string,
    postId: number,
    clientId: number,
    profile: string = ''
  ) {
    const content = `${fromUserName} posted on the team board`;
    return this.addnotifications(
      content,
      toUserId,
      NOTIFICATION_TYPES.FEEDBACK,
      postId,
      profile,
      clientId
    );
  }

  // ✅ ADD: Send action notification
  sendActionNotification(
    toUserId: number,
    actionTitle: string,
    clientId: number,
    actionId: number
  ) {
    const content = `New action item: ${actionTitle}`;
    return this.addnotifications(
      content,
      toUserId,
      NOTIFICATION_TYPES.ACTION,
      actionId,
      '',
      clientId
    );
  }

  // ✅ ADD: Send review notification
  sendReviewNotification(
    toUserId: number,
    rating: number,
    clientId: number,
    reviewId: number
  ) {
    const content = `You received a customer rating of ${rating}`;
    return this.addnotifications(
      content,
      toUserId,
      NOTIFICATION_TYPES.REVIEW,
      reviewId,
      '',
      clientId
    );
  }

  // ✅ ADD: Send training notification
  sendTrainingNotification(
    toUserId: number,
    fromUserName: string,
    trainingName: string,
    clientId: number,
    trainingId: number,
    profile: string = ''
  ) {
    const content = `${fromUserName} assigned training: ${trainingName}`;
    return this.addnotifications(
      content,
      toUserId,
      NOTIFICATION_TYPES.TRAINING,
      trainingId,
      profile,
      clientId
    );
  }

  // ✅ ADD: Send meeting notification
  sendMeetingNotification(
    toUserId: number,
    fromUserName: string,
    clientId: number,
    profile: string = ''
  ) {
    const content = `${fromUserName} would like to meet with you`;
    return this.addnotifications(
      content,
      toUserId,
      NOTIFICATION_TYPES.MEETING,
      null,
      profile,
      clientId
    );
  }

  sendAwardNotification(
    toUserId: number,
    fromUserName: string,
    awardName: string,
    clientId: number,
    profile: string = ''
  ) {
    return this.addnotifications(
      `${fromUserName} gave you the ${awardName} award`,
      toUserId,
      NOTIFICATION_TYPES.AWARD,
      null,
      profile,
      clientId
    );
  }

  sendPostNotification(
    toUserId: number,
    fromUserName: string,
    postId: number,
    clientId: number,
    isIdea: boolean,
    profile: string = ''
  ) {
    return this.addnotifications(
      isIdea ? `${fromUserName} shared a new idea` : `${fromUserName} posted a new announcement`,
      toUserId,
      isIdea ? NOTIFICATION_TYPES.IDEA : NOTIFICATION_TYPES.ANNOUNCEMENT,
      postId,
      profile,
      clientId
    );
  }

  // ✅ ADD: Mark single notification as read
  markAsRead(notificationId: number) {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.put(`${this.apiUrl}/notifications/${notificationId}/read`, {}, { headers });
  }

  // ✅ ADD: Mark all notifications as read
  markAllAsRead(userId: number) {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.put(`${this.apiUrl}/notifications/mark-all-read`, { user_id: userId }, { headers });
  }

  // ✅ ADD: Delete notification
  deleteNotification(notificationId: number) {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.delete(`${this.apiUrl}/notifications/${notificationId}`, { headers });
  }

  // ✅ ADD: Refresh total unread count
  refreshUnreadCount(userId: number) {
    this.getnotifications(userId).subscribe({
      next: (notifications) => {
        // Count notifications that are unread (you may need to adjust based on your data structure)
        const unreadCount = notifications.length;
        this.unreadCount$.next(unreadCount);
      },
      error: (err) => console.error('Error getting unread count:', err)
    });
  }

  // ✅ ADD: Get color for notification type
  getColorForType(type: number): string {
    return NOTIFICATION_COLORS[type] || '#6C757D';
  }

  // ✅ ADD: Get icon for notification type
  getIconForType(type: number): string {
    return NOTIFICATION_ICONS[type] || 'notifications-outline';
  }

  // ✅ ADD: Handle notification tap - navigate to appropriate page
  handleNotificationTap(notification: any) {
    switch (notification.type) {
      case NOTIFICATION_TYPES.CHAT:
        this.router.navigate(['/chat'], {
          queryParams: { userid: notification.postId || notification.post_id }
        });
        break;
      case NOTIFICATION_TYPES.KUDOS:
        this.router.navigate(['/profile']);
        break;
      case NOTIFICATION_TYPES.FEEDBACK:
        this.router.navigate(['/feedback-board']);
        break;
      case NOTIFICATION_TYPES.REVIEW:
        this.router.navigate(['/home-customer']);
        break;
      case NOTIFICATION_TYPES.TRAINING:
        this.router.navigate(['/profile']);
        break;
      case NOTIFICATION_TYPES.ACTION:
        this.router.navigate(['/home-action']);
        break;
      case NOTIFICATION_TYPES.AWARD:
        this.router.navigate(['/profile']);
        break;
      case NOTIFICATION_TYPES.IDEA:
      case NOTIFICATION_TYPES.ANNOUNCEMENT:
        this.router.navigate(['/feedback-board']);
        break;
      default:
        // Stay on notification page
        break;
    }
  }
}
